import { TicketEvent } from '@/constants/ticket-events'
import { TicketStatus } from '@/constants/ticket-status'
import { useAudioNotification } from '@/context/audio-notification-context'
import { useSocket } from '@/hooks/use-socket'
import { Message } from '@/interfaces/message'
import { Ticket } from '@/interfaces/ticket'
import {
  listAgentTicketsQuery,
  listDeskTicketsQuery,
  listFinishedTicketsQuery,
  listPendingTicketsQuery,
  listTicketMessagesQuery,
} from '@/queries/ticket.query'
import { useChatStore } from '@/stores/chat.store'
import { type QueryClient, useQueryClient } from '@tanstack/react-query'
import { useLocation } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import {
  isAgentInProgressTicket,
  isHumanInProgressTicket,
} from '../utils/ticket-routing'

function sortByUpdatedAtDesc(tickets: Ticket[]) {
  return [...tickets].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  )
}

function upsertTicket(tickets: Ticket[], ticket: Ticket): Ticket[] {
  const withoutTarget = tickets.filter((item) => item.id !== ticket.id)
  return sortByUpdatedAtDesc([ticket, ...withoutTarget])
}

function removeTicket(tickets: Ticket[], ticketId: number) {
  return tickets.filter((item) => item.id !== ticketId)
}

function syncQueryList(
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  ticket: Ticket,
  shouldInclude: boolean
) {
  queryClient.setQueryData<Ticket[]>(queryKey, (old = []) => {
    if (!shouldInclude) {
      return removeTicket(old, ticket.id)
    }

    return upsertTicket(old, ticket)
  })
}

function appendMessageToCache(
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  message: Message
) {
  if (queryClient.getQueryData<Message[]>(queryKey) === undefined) {
    return
  }

  const existingMessages = queryClient.getQueryData<Message[]>(queryKey) ?? []
  if (existingMessages.some((item) => item.id === message.id)) {
    return
  }

  queryClient.setQueryData<Message[]>(queryKey, [...existingMessages, message])
}

function incrementUnreadForTicket(
  ticketId: number,
  isOnChatsPage: boolean,
  isCurrentOpenTicket: boolean
) {
  if (isCurrentOpenTicket) {
    return
  }

  if (isOnChatsPage) {
    useChatStore.getState().incrementUnread(ticketId)
    return
  }

  useChatStore.setState((state) => {
    const unreadMessages = new Map(state.unreadMessages)
    const currentUnread = unreadMessages.get(ticketId) ?? 0
    unreadMessages.set(ticketId, currentUnread + 1)
    return { unreadMessages }
  })
}

function notifyPendingTicket(
  ticketStatus: TicketStatus | null,
  ticketId: number,
  playNewMessageSound: () => void,
  notifiedPendingTicketIds: Set<number>
) {
  if (ticketStatus !== TicketStatus.PENDING) {
    return
  }

  if (notifiedPendingTicketIds.has(ticketId)) {
    return
  }

  playNewMessageSound()
  notifiedPendingTicketIds.add(ticketId)
}

function resolveTicketStatusFromCache(
  ticketId: number,
  queryClient: QueryClient
) {
  const pendingTickets =
    queryClient.getQueryData<Ticket[]>(listPendingTicketsQuery.queryKey) ?? []
  if (pendingTickets.some((item) => item.id === ticketId)) {
    return TicketStatus.PENDING
  }

  const agentTickets =
    queryClient.getQueryData<Ticket[]>(listAgentTicketsQuery.queryKey) ?? []
  if (agentTickets.some((item) => item.id === ticketId)) {
    return TicketStatus.IN_PROGRESS
  }

  const deskTickets =
    queryClient.getQueryData<Ticket[]>(listDeskTicketsQuery.queryKey) ?? []
  if (deskTickets.some((item) => item.id === ticketId)) {
    return TicketStatus.IN_PROGRESS
  }

  const finishedTickets =
    queryClient.getQueryData<Ticket[]>(listFinishedTicketsQuery.queryKey) ?? []
  if (finishedTickets.some((item) => item.id === ticketId)) {
    return TicketStatus.CLOSED
  }

  return null
}

export function AuthenticatedChatRealtimeListener() {
  const queryClient = useQueryClient()
  const pathname = useLocation({ select: (location) => location.pathname })
  const currentTicketId = useChatStore((state) => state.currentTicketId)
  const { on, off } = useSocket()
  const { playNewMessageSound } = useAudioNotification()
  const notifiedPendingTicketIdsRef = useRef<Set<number>>(new Set())

  useEffect(() => {
    const syncTicketLists = (ticket: Ticket) => {
      syncQueryList(
        queryClient,
        listDeskTicketsQuery.queryKey,
        ticket,
        isHumanInProgressTicket(ticket)
      )

      syncQueryList(
        queryClient,
        listAgentTicketsQuery.queryKey,
        ticket,
        isAgentInProgressTicket(ticket)
      )

      syncQueryList(
        queryClient,
        listPendingTicketsQuery.queryKey,
        ticket,
        ticket.status === TicketStatus.PENDING
      )

      syncQueryList(
        queryClient,
        listFinishedTicketsQuery.queryKey,
        ticket,
        ticket.status === TicketStatus.CLOSED
      )

      if (ticket.status !== TicketStatus.PENDING) {
        notifiedPendingTicketIdsRef.current.delete(ticket.id)
      }
    }

    const handleMessageReceived = (...args: unknown[]) => {
      const payload = args[0] as { ticket?: Ticket; message?: Message }
      const message = payload?.message
      const ticket = payload?.ticket

      if (!message) return

      const resolvedTicketId = message.ticketId ?? ticket?.id
      if (!resolvedTicketId) return

      const normalizedMessage: Message = {
        ...message,
        ticketId: resolvedTicketId,
      }
      const messagesQueryKey =
        listTicketMessagesQuery(resolvedTicketId).queryKey

      appendMessageToCache(queryClient, messagesQueryKey, normalizedMessage)

      if (!normalizedMessage.fromMe) {
        const isOnChatsPage = pathname.startsWith('/chats')
        const isCurrentOpenTicket =
          isOnChatsPage && currentTicketId === resolvedTicketId

        incrementUnreadForTicket(
          resolvedTicketId,
          isOnChatsPage,
          isCurrentOpenTicket
        )

        const resolvedTicketStatus =
          ticket?.status ??
          resolveTicketStatusFromCache(resolvedTicketId, queryClient)

        notifyPendingTicket(
          resolvedTicketStatus,
          resolvedTicketId,
          playNewMessageSound,
          notifiedPendingTicketIdsRef.current
        )
      }

      if (ticket) {
        syncTicketLists({ ...ticket, lastMessage: normalizedMessage })
      }
    }

    const handleTicketEvent = (...args: unknown[]) => {
      const payload = args[0] as { ticket?: Ticket }
      if (!payload?.ticket) return
      syncTicketLists(payload.ticket)
    }

    on(TicketEvent.MESSAGE_RECEIVED, handleMessageReceived)
    on(TicketEvent.TICKET_CREATED, handleTicketEvent)
    on(TicketEvent.TICKET_UPDATED, handleTicketEvent)
    on(TicketEvent.TICKET_TRANSFERRED, handleTicketEvent)
    on(TicketEvent.TICKET_CLOSED, handleTicketEvent)

    return () => {
      off(TicketEvent.MESSAGE_RECEIVED, handleMessageReceived)
      off(TicketEvent.TICKET_CREATED, handleTicketEvent)
      off(TicketEvent.TICKET_UPDATED, handleTicketEvent)
      off(TicketEvent.TICKET_TRANSFERRED, handleTicketEvent)
      off(TicketEvent.TICKET_CLOSED, handleTicketEvent)
    }
  }, [currentTicketId, off, on, pathname, playNewMessageSound, queryClient])

  return null
}
